import { useEffect, useMemo, useState } from 'react'
import { mutate } from 'swr'
import { getRequest, patchRequest, putRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { compareStringConvertValue } from 'src/lib/utils/helpers'
import {
  getMissingLoyaltyFields,
  parseLoyaltyConfig,
  parseLoyaltyConfigForBackend,
  parsePaymentModeForRewardsConfig
} from 'src/lib/utils/integrationsHelpers'
import { LoyaltyConfig, LoyaltyFieldDef, RewardsProvider } from 'src/lib/types/integrations'

const emptyConfig = (fields: LoyaltyFieldDef[]): LoyaltyConfig => ({
  loyaltyProvider: '',
  isActive: false,
  metadata: Object.fromEntries(fields.map(field => [field.key, '']))
})

/**
 * Shared fetch/save/state logic for every bespoke loyalty connect form.
 * The form component owns its own JSX (copy, field layout); this hook owns the
 * plumbing that is identical across providers: load the rewards config, track
 * unsaved changes, validate required fields, and PATCH the config + reorder the
 * LOYALTY payment mode on save.
 */
export const useLoyaltyConfig = (provider: RewardsProvider, label: string, fields: LoyaltyFieldDef[]) => {
  const ISSUER_ID = process?.env?.NEXT_PUBLIC_APP_ISSUER_ID
  const { setLoaderProperties } = useLoader()
  const {
    getters: { merchant }
  } = useMerchant()

  const fieldKeys = useMemo(() => fields.map(field => field.key), [fields])

  const [config, setConfig] = useState<LoyaltyConfig>(() => emptyConfig(fields))
  const [savedConfig, setSavedConfig] = useState<LoyaltyConfig>(() => emptyConfig(fields))
  const [errorFields, setErrorFields] = useState<string[]>([])

  const configUrl = `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`

  useEffect(() => {
    const fetchConfig = async () => {
      if (!merchant?.accountId || !ISSUER_ID) return
      try {
        const response: any = await getRequest(configUrl, apiURI.REWARDS_ENGINE)
        const parsed = parseLoyaltyConfig(response?.data?.data, provider, fieldKeys)
        setConfig(parsed)
        setSavedConfig(parsed)
      } catch (error) {
        console.error(error)
      }
    }
    fetchConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?.accountId, ISSUER_ID])

  const settingsUpdated = useMemo(() => {
    return !compareStringConvertValue(savedConfig, config)
  }, [savedConfig, config])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        resolve(true)
      } else reject(false)
    })
  })

  const setActive = (isActive: boolean) => setConfig(prev => ({ ...prev, isActive }))

  const setFieldValue = (key: string, value: string) =>
    setConfig(prev => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }))

  const clearFieldError = (key: string) => setErrorFields(prev => prev.filter(field => field !== key))

  const handleSave = async () => {
    const missing = getMissingLoyaltyFields(config, fields)
    if (missing.length > 0) {
      setErrorFields(missing)
      const firstMissing = fields.find(field => field.key === missing[0])
      errorToast(`${firstMissing?.label ?? 'A required field'} is required`)
      return
    }

    try {
      setLoaderProperties({ isLoaderVisible: true })
      const [rewardsResponse, paymentModesResponse]: any = await Promise.all([
        patchRequest(configUrl, apiURI.REWARDS_ENGINE, parseLoyaltyConfigForBackend(config, provider)),
        putRequest(
          `/fms/v1/merchant-gateways/reorder-payment-modes`,
          apiURI.FLO_CHECKOUT,
          parsePaymentModeForRewardsConfig(provider, config.isActive)
        )
      ])

      const parsed = parseLoyaltyConfig(rewardsResponse?.data?.data, provider, fieldKeys)
      setConfig(parsed)
      setSavedConfig(parsed)
      if (paymentModesResponse) mutate('/fms/v1/merchant-payment-modes')

      successToast(`${label} settings saved successfully`)
    } catch (error) {
      console.error(error)
      errorToast(`Failed to save ${label} settings`)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const handleDiscard = () => {
    setConfig(savedConfig)
    setErrorFields([])
  }

  return {
    config,
    settingsUpdated,
    errorFields,
    setActive,
    setFieldValue,
    clearFieldError,
    handleSave,
    handleDiscard
  }
}
